<template>
  <BaseForm @submit="handleSubmitReview">
    <div class="form-row form-order">
      <div class="form__seller">
        <NuxtLinkLocale
          v-if="seller.small_logo"
          :to="sellerLink"
          class="form__seller-logo"
          @click="handleClose"
        >
          <BaseImage
            size="100x100"
            :name="seller.small_logo"
            :alt="seller.name"
          />
        </NuxtLinkLocale>

        <div>
          <p class="form__seller-title">
            {{ $t('seller.seller') }}
          </p>
          <BaseButton
            theme="link"
            :to="sellerLink"
            class="form__seller-name"
            @click="handleClose"
          >
            {{ seller.name }}
          </BaseButton>
        </div>
      </div>

      <div class="form__product-details">
        <p class="form__description">
          {{ $t('reviews.order_from') }} {{ lastOrderDate }},
          <br>
          {{ $t('seller.amount_of') }}
          <b class="form__price">{{ lastOrder.price_total }} <span class="icon-hryvnia" />
            <template v-if="usedBonuses > 0">
              + {{ lastOrder.bonus_discount_total }}
              <span class="icon-synthetic" />
            </template>
          </b>
        </p>

        <CommonImagesCount
          :all-images="lastOrder.images"
          :quantity="lastOrder.items_count"
        />
      </div>
    </div>

    <div class="form-row form-rating-overall">
      <h5>{{ $t('seller.collaboration_experience') }}</h5>

      <CommonRatingOverall
        v-model="formData.rating_product_overall"
      />

      <div v-if="$v.rating_product_overall.$invalid && $v.$error" class="form-row__error">
        {{ $t('seller.indicate_overall_rating') }}
      </div>
    </div>

    <!--CommonStars-->
    <div class="form-row form-rating">
      <h5>{{ $t('seller.refinement_assessment') }}</h5>

      <ul class="rating__list">
        <li class="rating__line">
          <div class="rating__description">
            {{ $t('seller.goods_match_the_description') }}
          </div>
          <CommonStars
            :rating="formData.rating_product_description_fits"
            size="large-mobile"
            selectable
            @select="formData.rating_product_description_fits = $event"
          />
        </li>

        <li class="rating__line">
          <div class="rating__description">
            {{ $t('seller.quality_of_packaging_and_delivery') }}
          </div>
          <CommonStars
            :rating="formData.rating_delivery"
            size="large-mobile"
            selectable
            @select="formData.rating_delivery = $event"
          />
        </li>

        <li class="rating__line">
          <div class="rating__description">
            {{ $t('seller.service_level') }}
          </div>
          <CommonStars
            :rating="formData.rating_service"
            size="large-mobile"
            selectable
            @select="formData.rating_service = $event"
          />
        </li>
      </ul>
    </div>
    <!---->

    <!--Comment-->
    <div class="form-row">
      <BaseTextarea
        v-model="formData.comment"
        :placeholder="$t('seller.comment')"
        :error="$v.comment.$error"
        autocomplete="off"
        class="form-row__textarea"
        required
      >
        <template #errors>
          <BaseInputError v-if="$v.comment.required.$invalid && $v.$error">
            {{ $t('common.field_errors.required') }}
          </BaseInputError>
          <BaseInputError v-else-if="$v.comment.minLength.$invalid && $v.$error">
            {{ $t('common.field_errors.min_length', 4) }}
          </BaseInputError>
          <BaseInputError v-else-if="$v.comment.maxLength.$invalid && $v.$error">
            {{ $t('common.field_errors.max_length', 2200) }}
          </BaseInputError>
          <BaseInputServerErrors :errors="serverErrors" field="comment" />
        </template>
      </BaseTextarea>
    </div>
    <!---->

    <div class="form-row">
      <ImagesLoader
        id="seller-reviews-form-loader"
        :images="formData.images"
        @filesOnReadyToLoad="formData.images = $event.files"
      />
    </div>

    <!--Username-->
    <div class="form-row">
      <div v-if="!hasUsername">
        <BaseInput
          v-model="formData.author_username"
          :placeholder="$t('reviews.username')"
          :error="$v.author_username.$error"
          autocomplete="off"
          required
        >
          <template #errors>
            <BaseInputError
              v-if="$v.author_username.required.$invalid && $v.$error"
            >
              {{ $t('common.field_errors.required') }}
            </BaseInputError>
            <BaseInputError v-else-if=" $v.author_username.minLength.$invalid && $v.$error">
              {{ $t('common.field_errors.min_length', 3) }}
            </BaseInputError>
            <BaseInputError v-else-if="$v.author_username.maxLength.$invalid && $v.$error">
              {{ $t('common.field_errors.max_length', 255) }}
            </BaseInputError>
            <BaseInputError v-else-if="$v.author_username.withoutSpaces.$invalid && $v.$error">
              {{ $t('common.field_errors.username_without_spaces') }}
            </BaseInputError>
            <BaseInputServerErrors
              :errors="serverErrors"
              field="author_username"
            />
          </template>
        </BaseInput>
        <p class="form-row__text" v-html="$t('reviews.username_policy')" />
      </div>

      <p v-else class="form-row__text form-row__text-single">
        {{ $t('reviews.review_send_in') }}: {{ username }}
      </p>
    </div>
    <!---->

    <div v-if="showReCaptcha" class="form-row">
      <Recaptcha />
    </div>

    <div class="form-buttons">
      <BaseButton
        theme="secondary"
        full-width
        @click="handleClose"
      >
        {{ $t('common.cancel') }}
      </BaseButton>

      <BaseButton
        type="submit"
        :loading="fetching"
        full-width
        outline
      >
        {{ $t('reviews.send_review_button') }}
      </BaseButton>
    </div>
  </BaseForm>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, maxLength, minLength } from '@vuelidate/validators'
import { withoutSpaces } from '@/utils/vuelidate'
import { modalCreateReviewSuccessId } from '@/constants/modalNames'
import type { OrderRecord } from '~/types/checkout'
import type { SellerReview } from '~/types/review'
import type { ProductOfferSeller } from '~/types/product'

type Props = {
  sellerSlug: string
  lastOrder: OrderRecord
}
type Emits = {
  (e: 'close'): void
  (e: 'review-success', review: SellerReview): void
}
const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const authUserStore = useAuthUserStore()
const route = useRoute()
const router = useRouter()
const { $toast, $vfm, $recaptcha, $dayjs, $api } = useNuxtApp()
const i18n = useI18n()
const localePath = useLocalePath()

const fetching = shallowRef(false)
const serverErrors = shallowRef({})
const showReCaptcha = shallowRef(false)

const defaultFormData = () => ({
  author_username: '',
  comment: '',
  images: [] as File[],
  rating_product_overall: null as null | number,
  rating_product_description_fits: 0,
  rating_delivery: 0,
  rating_service: 0
})

const formData = reactive(defaultFormData())

const rules = {
  author_username: {
    required: requiredIf(() => !hasUsername.value),
    minLength: minLength(3),
    maxLength: maxLength(255),
    withoutSpaces
  },
  comment: {
    required,
    minLength: minLength(4),
    maxLength: maxLength(2200)
  },
  rating_product_overall: {
    validator(value: number | null) {
      return value !== null && value >= 0 && value <= 2
    }
  }
}

const $v = useVuelidate<any>(rules, formData)

const username = computed(() => {
  return authUserStore.data.username
})

const hasUsername = computed(() => {
  return !!username.value
})

const seller = computed((): ProductOfferSeller => {
  return props.lastOrder.seller
})

const sellerLink = computed(() => {
  return `/seller/${props.sellerSlug}/`
})

const usedBonuses = computed(() => {
  return props.lastOrder.bonus_discount_total || 0
})

const lastOrderDate = computed(() => {
  return $dayjs(props.lastOrder.created_at)
    .format('DD.MM.YYYY HH:mm')
})

const createReview = (formData: any) => {
  fetching.value = true

  $api<SellerReview>(`/api/sellers/${encodeURIComponent(props.sellerSlug)}/reviews/`, {
    method: 'POST',
    body: formData
  })
    .then((review) => {
      if (review) {
        handleSuccess(review)
      }
      fetching.value = false
      showReCaptcha.value = false
    })
    .catch((e) => {
      serverErrors.value = e.response.data
      fetching.value = false

      if (e.response.status === 401) {
        $toast.error(i18n.t('common.not_authorized'))
        router.push({
          path: localePath('/auth/login'),
          query: { next: `${router.currentRoute.value.path}#reviews` }
        })
      } else if (e.response.status === 409) {
        showReCaptcha.value = true
      }
    })
}

const handleSubmitReview = async(): Promise<void> => {
  $v.value.$touch()
  if ($v.value.$invalid) {
    return
  }

  const actionData: object = {
    ...formData,
    order: props.lastOrder.uuid
  }

  const fd = new FormData()

  for (const [key, value] of Object.entries(actionData)) {
    if (Array.isArray(value)) {
      value.forEach((item) => fd.append(key, item))
    } else if (value !== null) {
      fd.append(key, value)
    } else {
      fd.append(key, '')
    }
  }

  if (showReCaptcha.value) {
    try {
      const token = await $recaptcha.getResponse()
      const { success } = await $api<{ success: boolean }>('/api/common/recaptcha/', {
        method: 'POST',
        body: { token }
      })

      success
        ? createReview(fd)
        : $toast.error(i18n.t('common.captcha_failure'))
    } catch (e) {
      $toast.error(i18n.t('common.field_errors.recaptcha_required'))
    }
  } else {
    createReview(fd)
  }
}

const resetData = () => {
  Object.assign(formData, defaultFormData())
  $v.value.$reset()
}

const handleClose = () => {
  resetData()
  emit('close')
}

const handleSuccess = (review: SellerReview) => {
  emit('review-success', review)
  handleClose()
  $vfm.open(modalCreateReviewSuccessId)
}

onMounted(() => {
  const {
    create_seller_review: createSellerReview,
    order,
    rate
  } = unref(route).query

  if (rate && createSellerReview && props.lastOrder.uuid === order) {
    formData.rating_product_overall = +rate
  }
})

onBeforeUnmount(handleClose)
</script>

<style lang="scss" scoped>
.icon-synthetic {
  font-size: 1rem;
}
.form-row {
  display: block;
  &__textarea {
    @media only screen and (max-width: 1024px) {
      @supports (-webkit-appearance: none) {
        max-height: 30vh;
      }
    }
  }
}

.rating {
  &__list {
    list-style-type: none;
    padding: 0;
  }

  &__line {
    padding: 0;
    line-height: $line-height-18;
    @media only screen and (max-width: 740px) {
      margin: 0 0 1.6rem;
    }
    @media only screen and (min-width: 740px) {
      display: flex;
      margin: 0 0 1.2rem;
    }
    &:last-of-type {
      margin: 0;
    }
  }

  &__description {
    @media only screen and (max-width: 740px) {
      margin-bottom: 0.8rem;
    }
    @media only screen and (min-width: 740px) {
      min-width: 21rem;
      margin-right: 2rem;
    }
  }
}

.form {
  &-order,
  &-rating-overall {
    @media only screen and (max-width: 740px) {
      margin-bottom: 2.5rem;
    }
    @media only screen and (min-width: 740px) {
      margin-bottom: 3rem;
    }
  }

  &-order {
    @media only screen and (min-width: 740px) {
      display: flex;
      justify-content: space-between;
    }
  }

  &-rating {
    margin-bottom: 2.5rem;
  }

  &__seller {
    display: flex;

    &-logo {
      display: block;
      width: 4.4rem;
      height: 4.4rem;
      overflow: hidden;
      border-radius: 4px;
      margin-right: 1rem;

      img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    &-title {
      margin-bottom: 0.4rem;
    }

    &-name {
      font-size: $h5-font-size;
      font-weight: 500;
    }
  }

  &__product-details {
    display: flex;
    justify-content: space-between;
    color: $secondary-font-color;
    @media only screen and (max-width: 740px) {
      margin-top: 1.5rem;
    }
  }

  &__description {
    margin: 0 1.2rem 0 0;
    line-height: $h6-line-height;
  }

  &__price {
    color: $font-color;
  }
}

.form-row {
  &__error {
    margin-top: 0.5rem;
    color: $error-color;
  }
}
</style>
